import { useCallback } from 'react';

// Declare gtag and dataLayer types
declare global {
  interface Window {
    gtag: (
      command: 'event' | 'config' | 'set',
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer: Record<string, unknown>[];
  }
}

export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_brand?: string;
  item_variant?: string;
}

// Push events to dataLayer for GTM
const pushToDataLayer = (event: string, ecommerce: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Clear previous ecommerce data
    window.dataLayer.push({ ecommerce: null });
    // Push new ecommerce event
    window.dataLayer.push({
      event,
      ecommerce,
    });
  }
};

export const useAnalytics = () => {
  const isGtagAvailable = useCallback(() => {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }, []);

  const trackPageView = useCallback((pagePath: string, pageTitle?: string) => {
    if (!isGtagAvailable()) return;
    
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }, [isGtagAvailable]);

  const trackViewItem = useCallback((item: AnalyticsItem, currency = 'USD') => {
    const itemData = {
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: 1,
      item_category: item.item_category,
      item_brand: item.item_brand,
      item_variant: item.item_variant,
    };

    // Push to GTM dataLayer
    pushToDataLayer('view_item', {
      currency,
      value: item.price,
      items: [itemData],
    });

    // Also send via gtag if available
    if (isGtagAvailable()) {
      window.gtag('event', 'view_item', {
        currency,
        value: item.price,
        items: [itemData],
      });
    }
  }, [isGtagAvailable]);

  const trackAddToCart = useCallback((item: AnalyticsItem, currency = 'USD') => {
    const itemData = {
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
      item_category: item.item_category,
      item_brand: item.item_brand,
      item_variant: item.item_variant,
    };

    // Push to GTM dataLayer
    pushToDataLayer('add_to_cart', {
      currency,
      value: item.price * item.quantity,
      items: [itemData],
    });

    // Also send via gtag if available
    if (isGtagAvailable()) {
      window.gtag('event', 'add_to_cart', {
        currency,
        value: item.price * item.quantity,
        items: [itemData],
      });
    }
  }, [isGtagAvailable]);

  const trackRemoveFromCart = useCallback((item: AnalyticsItem, currency = 'USD') => {
    const itemData = {
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    };

    // Push to GTM dataLayer
    pushToDataLayer('remove_from_cart', {
      currency,
      value: item.price * item.quantity,
      items: [itemData],
    });

    // Also send via gtag if available
    if (isGtagAvailable()) {
      window.gtag('event', 'remove_from_cart', {
        currency,
        value: item.price * item.quantity,
        items: [itemData],
      });
    }
  }, [isGtagAvailable]);

  const trackBeginCheckout = useCallback((
    items: AnalyticsItem[],
    value: number,
    currency = 'USD'
  ) => {
    const itemsData = items.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    }));

    // Push to GTM dataLayer
    pushToDataLayer('begin_checkout', {
      currency,
      value,
      items: itemsData,
    });

    // Also send via gtag if available
    if (isGtagAvailable()) {
      window.gtag('event', 'begin_checkout', {
        currency,
        value,
        items: itemsData,
      });
    }
  }, [isGtagAvailable]);

  const trackPurchase = useCallback((
    transactionId: string,
    items: AnalyticsItem[],
    value: number,
    currency = 'USD',
    shipping?: number,
    tax?: number
  ) => {
    const itemsData = items.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    }));

    const ecommerceData = {
      transaction_id: transactionId,
      currency,
      value,
      shipping,
      tax,
      items: itemsData,
    };

    // Push to GTM dataLayer
    pushToDataLayer('purchase', ecommerceData);

    // Also send via gtag if available
    if (isGtagAvailable()) {
      window.gtag('event', 'purchase', ecommerceData);
    }
  }, [isGtagAvailable]);

  const trackSearch = useCallback((searchTerm: string) => {
    if (!isGtagAvailable()) return;

    window.gtag('event', 'search', {
      search_term: searchTerm,
    });
  }, [isGtagAvailable]);

  const trackViewItemList = useCallback((
    listId: string,
    listName: string,
    items: AnalyticsItem[]
  ) => {
    if (!isGtagAvailable()) return;

    window.gtag('event', 'view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      items: items.map((item, index) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        price: item.price,
        index,
      })),
    });
  }, [isGtagAvailable]);

  const trackSelectItem = useCallback((
    listId: string,
    listName: string,
    item: AnalyticsItem
  ) => {
    if (!isGtagAvailable()) return;

    window.gtag('event', 'select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [{
        item_id: item.item_id,
        item_name: item.item_name,
        price: item.price,
      }],
    });
  }, [isGtagAvailable]);

  return {
    trackPageView,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchase,
    trackSearch,
    trackViewItemList,
    trackSelectItem,
  };
};

// Standalone tracking functions for use outside React components
// Push to GTM dataLayer
const pushToDataLayerStandalone = (event: string, ecommerce: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
  }
};

export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  // Push to GTM dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...params });
  }
  // Also send via gtag
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

export const trackAddToCartEvent = (item: AnalyticsItem, currency = 'USD') => {
  const ecommerceData = {
    currency,
    value: item.price * item.quantity,
    items: [{
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    }],
  };

  pushToDataLayerStandalone('add_to_cart', ecommerceData);
  trackEvent('add_to_cart', ecommerceData);
};

export const trackRemoveFromCartEvent = (item: AnalyticsItem, currency = 'USD') => {
  const ecommerceData = {
    currency,
    value: item.price * item.quantity,
    items: [{
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    }],
  };

  pushToDataLayerStandalone('remove_from_cart', ecommerceData);
  trackEvent('remove_from_cart', ecommerceData);
};

// Additional standalone functions for GTM
export const trackViewItemEvent = (item: AnalyticsItem, currency = 'USD') => {
  const ecommerceData = {
    currency,
    value: item.price,
    items: [{
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: 1,
    }],
  };

  pushToDataLayerStandalone('view_item', ecommerceData);
};

export const trackBeginCheckoutEvent = (items: AnalyticsItem[], value: number, currency = 'USD') => {
  const ecommerceData = {
    currency,
    value,
    items: items.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    })),
  };

  pushToDataLayerStandalone('begin_checkout', ecommerceData);
};

export const trackPurchaseEvent = (
  transactionId: string,
  items: AnalyticsItem[],
  value: number,
  currency = 'USD',
  shipping?: number,
  tax?: number
) => {
  const ecommerceData = {
    transaction_id: transactionId,
    currency,
    value,
    shipping,
    tax,
    items: items.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
    })),
  };

  pushToDataLayerStandalone('purchase', ecommerceData);
};
